import './FastClickContainer.css';

import * as React from 'react';

import { classNameMerger } from '../../utils/util';

function assertNever(): never {
    throw new Error('never');
}

export interface FastClickContainerProps extends React.HTMLAttributes<HTMLDivElement> {
    /**
     * Provides a fast click and ripple effect on touch devices and optional ripple for mouse
     */
    onFastClick: (e: FastClickEvent) => void;
    /**
     * Provides long press for contextMenu both on touch and mouse and includes normal right click
     */
    onFastContextMenu?: (e: FastClickEvent) => void;
    innerRef?: React.RefObject<HTMLDivElement>;
    behaviour?: 'FORCE_RIPPLE' | 'PREVENT_RIPPLE';

    /**
     * Defaults to DARK
     */
    rippleStyle?: 'DARK' | 'LIGHT';

    /**
     * Prevents long press from firing context menu
     */
    preventLongPress?: boolean;

    /**
     * Sets custom "data-testid" property. Should be as unique as possible. Used for automated testing purposes only.
     */
    testID: string;
}

export interface FastClickContainerState {
    spanStyles: React.CSSProperties[];
}

export type FastClickEvent = MouseUp | TouchEnd | MouseDown | TouchStart;

export interface TouchEnd {
    kind: 'TouchEnd';
    event: React.TouchEvent;
}

export interface MouseUp {
    kind: 'MouseUp';
    event: React.MouseEvent;
}

export interface TouchStart {
    kind: 'TouchStart';
    event: React.TouchEvent;
}

export interface MouseDown {
    kind: 'MouseDown';
    event: React.MouseEvent;
}

export function getPosition(e: FastClickEvent): { x: number; y: number } {
    switch (e.kind) {
        case 'MouseDown':
        case 'MouseUp':
            return { x: e.event.clientX, y: e.event.clientY };
        case 'TouchEnd':
            return { x: e.event.changedTouches[0].clientX, y: e.event.changedTouches[0].clientY };
        case 'TouchStart':
            return { x: e.event.touches[0].clientX, y: e.event.touches[0].clientY };
        default:
            return assertNever();
    }
}

export function getTarget(e: FastClickEvent): HTMLElement {
    switch (e.kind) {
        case 'MouseDown':
        case 'MouseUp':
            return e.event.target as HTMLElement;
        case 'TouchEnd':
            return e.event.changedTouches[0].target as HTMLElement;
        case 'TouchStart':
            return e.event.touches[0].target as HTMLElement;
        default:
            return assertNever();
    }
}

export class FastClickContainer extends React.Component<FastClickContainerProps, FastClickContainerState> {
    private shouldCountAsClick: boolean;
    private rippleTimeoutId: number | undefined;
    private longPressTimeoutId: number | undefined;
    private initialCoords: { x: number; y: number };
    private longPressOccurred: boolean = false;

    constructor(props: FastClickContainerProps) {
        super(props);
        this.shouldCountAsClick = false;
        this.initialCoords = { x: 0, y: 0 };

        this.state = {
            spanStyles: []
        };
    }

    public componentWillUnmount(): void {
        clearTimeout(this.rippleTimeoutId);
        clearTimeout(this.longPressTimeoutId);
    }

    public render() {
        const { onFastClick, innerRef, rippleStyle, behaviour, preventLongPress, onFastContextMenu, testID, ...rest } =
            {
                ...this.props
            };

        return (
            <div
                {...rest}
                ref={this.props.innerRef}
                className={classNameMerger(this.props.className, 'FastClickContainer_root')}
                data-testid={testID}
                role="button"
                onContextMenu={this.handleContextMenu}
                onMouseDown={this.handleMouseDown}
                onMouseLeave={this.handleMouseLeave}
                onMouseUp={this.handleMouseUp}
                onTouchEnd={this.handleTouchEnd}
                onTouchMove={this.handleTouchMove}
                onTouchStart={this.handleTouchStart}
            >
                {this.props.children}
                <div className="FastClickContainer_rippleContainer">{this.renderRippleSpan()}</div>
            </div>
        );
    }

    private handleContextMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (this.props.onFastContextMenu !== undefined) {
            e.preventDefault();
            e.stopPropagation();
            this.props.onFastContextMenu({ event: e, kind: 'MouseDown' });
        }

        // This is for when moving from mouse to touch AND preventLongPress is true, to ignore touchend
        this.shouldCountAsClick = false;
    };

    private handleTouchEnd = (e: React.TouchEvent) => {
        clearTimeout(this.longPressTimeoutId);

        if (this.longPressOccurred) {
            this.handleLongPressOccurred(e);
        } else if (this.shouldCountAsClick && e.touches.length === 0 && e.changedTouches.length === 1) {
            e.stopPropagation();
            e.preventDefault();

            this.props.onFastClick({ kind: 'TouchEnd', event: e });
        }

        this.shouldCountAsClick = false;
    };

    private handleMouseUp = (e: React.MouseEvent) => {
        clearTimeout(this.longPressTimeoutId);

        if (this.longPressOccurred) {
            this.handleLongPressOccurred(e);
        } else if (this.shouldCountAsClick) {
            e.stopPropagation();
            e.preventDefault();

            this.props.onFastClick({ kind: 'MouseUp', event: e });
        }

        this.shouldCountAsClick = false;
    };

    private handleTouchMove = (e: React.TouchEvent) => {
        if (!this.shouldCountAsClick) {
            return;
        }

        const x1 = e.touches[0].clientX;
        const y1 = e.touches[0].clientY;
        const threshold = 100;
        const dx = x1 - this.initialCoords.x;
        const dy = y1 - this.initialCoords.y;

        if (dx * dx + dy * dy > threshold) {
            this.cleanUp();
            this.shouldCountAsClick = false;
        }
    };

    private handleMouseLeave = () => {
        if (!this.shouldCountAsClick) {
            return;
        }

        this.cleanUp();
        this.shouldCountAsClick = false;
    };

    private handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
        if (e.touches.length > 1) {
            return;
        }

        const fastEvent: FastClickEvent = { event: e, kind: 'TouchStart' };

        this.setInitialCoords(fastEvent);
        this.startClick(fastEvent);

        if (this.props.behaviour !== 'PREVENT_RIPPLE') {
            this.showRipple(fastEvent);
        }

        if (this.props.onTouchStart !== undefined) {
            this.props.onTouchStart(e);
        }
    };

    private handleMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.button !== 0) {
            return;
        }

        const fastMouseEvent: FastClickEvent = { event: e, kind: 'MouseDown' };

        this.startClick(fastMouseEvent);

        if (this.props.behaviour === 'FORCE_RIPPLE') {
            this.showRipple(fastMouseEvent);
        }

        if (this.props.onMouseDown !== undefined) {
            this.props.onMouseDown(e);
        }
    };

    private setInitialCoords(e: FastClickEvent) {
        const point = getPosition(e);

        this.initialCoords = { x: point.x, y: point.y };
    }

    private startClick(e: FastClickEvent) {
        this.shouldCountAsClick = true;

        // Have the event fired on long click
        e.event.persist();
        this.longPressTimer(e);

        // Prevent stacked FastClickContainers from showing ripple
        e.event.stopPropagation();
    }

    private longPressTimer(e: FastClickEvent) {
        clearTimeout(this.longPressTimeoutId);

        this.longPressTimeoutId = window.setTimeout(() => {
            if (
                this.shouldCountAsClick &&
                this.props.onFastContextMenu !== undefined &&
                this.props.preventLongPress !== true
            ) {
                if (e.event.cancelable) {
                    e.event.preventDefault();
                } else {
                    // Sometimes events can not be cancelled,
                    // So let the event continue but ignore the NEXT event
                    this.longPressOccurred = true;
                }

                e.event.stopPropagation();
                this.props.onFastContextMenu(e);
                this.shouldCountAsClick = false;
            }
        }, 200);
    }

    private handleLongPressOccurred(e: React.MouseEvent | React.TouchEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.longPressOccurred = false;
    }

    private showRipple = (e: FastClickEvent) => {
        this.callCleanUp(500);
        const rippleContainer: HTMLElement = e.event.currentTarget as HTMLElement;
        const size = rippleContainer.offsetWidth;
        const pos = rippleContainer.getBoundingClientRect() as DOMRect;
        let { x, y } = getPosition(e);

        y = y - pos.y - size / 2;
        x = x - pos.x - size / 2;
        const spanStyles = { top: `${y}px`, left: `${x}px`, height: `${size}px`, width: `${size}px` };

        this.setState({ spanStyles: this.state.spanStyles.concat(spanStyles) });
    };

    private renderRippleSpan = () => {
        if (this.state.spanStyles.length > 0) {
            return this.state.spanStyles.map((spanStyle, index) => {
                return (
                    <span
                        key={`spanCount_${index}`}
                        className={`FastClickContainer_ripple ${
                            this.props.rippleStyle === 'LIGHT'
                                ? 'FastClickContainer_rippleLight'
                                : 'FastClickContainer_rippleDark'
                        }`}
                        style={spanStyle}
                    />
                );
            });
        }

        return null;
    };
    private cleanUp = () => {
        this.setState({ spanStyles: [] });
    };
    private callCleanUp = (delay: number) => {
        if (this.props.behaviour === 'PREVENT_RIPPLE') {
            return;
        }

        clearTimeout(this.rippleTimeoutId);
        this.rippleTimeoutId = window.setTimeout(this.cleanUp, delay);
    };
}
